import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from './index';
import type { FreeRewriteStatus, FreeRewriteUploadInfo } from '../types/freeRewrite';
import { fetchUserProfile } from '../utils/api';

interface FreeRewriteState {
  /** Статус бонуса из /profile/me */
  status: FreeRewriteStatus | null;
  /** Информация о бонусе из последнего upload */
  uploadInfo: FreeRewriteUploadInfo | null;
  /** Загрузка статуса выполняется */
  loading: boolean;
}

const initialState: FreeRewriteState = {
  status: null,
  uploadInfo: null,
  loading: false,
};

/**
 * Загрузить статус бесплатного рерайта из /profile/me.
 * Вызывается автоматически после авторизации (из useSocket).
 */
export const loadFreeRewriteStatus = createAsyncThunk(
  'freeRewrite/loadStatus',
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    // Не загружаем повторно, если статус уже получен
    if (state.freeRewrite.status !== null) return state.freeRewrite.status;

    try {
      const res = await fetchUserProfile();
      return res?.data?.freeRewrite ?? null;
    } catch (err: any) {
      // Не критичная ошибка — просто не покажем бонус
      return rejectWithValue(err?.message || 'Не удалось загрузить статус бонуса');
    }
  }
);

const freeRewriteSlice = createSlice({
  name: 'freeRewrite',
  initialState,
  reducers: {
    /** Установить статус бонуса (из /profile/me) */
    setFreeRewriteStatus: (state, action: PayloadAction<FreeRewriteStatus | null>) => {
      state.status = action.payload;
    },
    /** Установить info из upload-ответа */
    setFreeRewriteUploadInfo: (state, action: PayloadAction<FreeRewriteUploadInfo | null>) => {
      state.uploadInfo = action.payload;
    },
    /** Бонус использован — обновить статус */
    consumeFreeRewrite: (state, action: PayloadAction<{ pages: number; usedAt?: string }>) => {
      if (state.status) {
        state.status.eligible = false;
        state.status.usedAt = action.payload.usedAt || new Date().toISOString();
      }
      state.uploadInfo = null;
    },
    /** Полный сброс (при logout) */
    resetFreeRewrite: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadFreeRewriteStatus.pending, (state) => {
        state.loading = true;
      })
      .addCase(loadFreeRewriteStatus.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.status = action.payload;
        }
      })
      .addCase(loadFreeRewriteStatus.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const {
  setFreeRewriteStatus,
  setFreeRewriteUploadInfo,
  consumeFreeRewrite,
  resetFreeRewrite,
} = freeRewriteSlice.actions;

// Селекторы
export const selectFreeRewrite = (state: RootState) => state.freeRewrite;
export const selectFreeRewriteStatus = (state: RootState) => state.freeRewrite.status;
export const selectFreeRewriteUploadInfo = (state: RootState) => state.freeRewrite.uploadInfo;

/** Доступен ли бесплатный рерайт (enabled + eligible) */
export const selectIsFreeRewriteAvailable = (state: RootState): boolean => {
  const s = state.freeRewrite.status;
  return Boolean(s?.enabled && s?.eligible);
};

/** Количество бесплатных страниц */
export const selectFreeRewritePages = (state: RootState): number => {
  return state.freeRewrite.status?.pages ?? 0;
};

export default freeRewriteSlice.reducer;
